import React from "react";
import {
  ErrorMessageContainer,
  FloatingComponentSpan,
  InputBorderSpan,
  InputContainer,
  InputTitle,
  LeadingComponentSpan,
  Loader,
  PropelInput,
} from "../Input.styles";
import { InputBaseProps } from "./InputBase.types";
import { ThemePalette } from "../../../../theme";
import { SpinLoader } from "../../react-loader/spin-loader/SpinLoader";
import { SpinLoaderTypes } from "../../react-loader/spin-loader/SpinLoader.types";

export const InputBase = <T extends object>(props: InputBaseProps<T>) => {
  const {
    initialValue,
    borderType,
    inputLabel,
    inputValue,
    type,
    placeHolder,
    onInputValueChange,
    keyProp,
    autoComplete,
    requiredValidator,
    inputValidator,
    inputFormatter,
    isRequired,
    onFocus,
    onClick,
    onBlur = () => true,
    onclickFloatingComponent,
    onLeadingComponentClick,
    onKeyDown,
    afterSubmit,
    floatingComponent,
    leadingComponent,
    min,
    max,
    step,
    isReadOnly,
    isDisabled,
    isCleared,
    showLoadingState,
    isAutoFocus,
    title,
  } = props;
  const inputRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (afterSubmit !== undefined && !initialValue) {
      setValidInput(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afterSubmit]);

  React.useEffect(() => {
    if (isAutoFocus && inputRef?.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [validInput, setValidInput] = React.useState<boolean>(false);
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValueFromField = e.target.value;
    const formattedInputValue = inputFormatter
      ? inputFormatter(inputValueFromField, inputValue)
      : inputValueFromField;
    onInputValueChange(formattedInputValue, keyProp);
  };
  const errorMessage = () => {
    if (isInputValueInvalid() && inputValue !== "" && inputValidator) {
      return (
        <ErrorMessageContainer theme={ThemePalette}>
          {inputValidator.errorText}
        </ErrorMessageContainer>
      );
    } else {
      return null;
    }
  };
  const requiredMessage = () => {
    if (inputValue === "" && requiredValidator && validInput) {
      return (
        <ErrorMessageContainer theme={ThemePalette}>
          {requiredValidator.requiredText}
        </ErrorMessageContainer>
      );
    } else {
      return null;
    }
  };
  const floatingComponentField = () => {
    return (
      <FloatingComponentSpan onClick={onclickFloatingComponent}>
        {floatingComponent}
      </FloatingComponentSpan>
    );
  };
  const leadingComponentField = () => {
    return (
      <LeadingComponentSpan onClick={onLeadingComponentClick}>
        {leadingComponent}
      </LeadingComponentSpan>
    );
  };
  const onInputBlur = () => {
    setValidInput(true);
    onBlur();
  };
  const onInputInvalid = () => {
    setValidInput(true);
  };
  const isInputValueInvalid = () => {
    if (validInput) {
      if (inputValue === "" && requiredValidator) {
        return true;
      } else if (inputValue !== "" && inputValidator) {
        return !inputValidator.validate(inputValue);
      }
    }
    return false;
  };

  const inputComponent = () => {
    const required = !!isRequired;
    const hasError = isInputValueInvalid();
    let minMax = {};
    if (type === "number") {
      minMax = {
        max: max ?? 0,
        min: min ?? 0,
        step: step ?? 1,
      };
    }
    return (
      <InputContainer>
        <PropelInput
          aria-label={props.inputLabel}
          ref={inputRef}
          theme={ThemePalette}
          type={type}
          value={inputValue}
          onChange={onInputChange}
          placeholder={placeHolder ?? ""}
          autoComplete={autoComplete ? "on" : "new-fields"}
          onBlur={onInputBlur}
          required={required}
          onInvalid={onInputInvalid}
          pattern={hasError ? `/error/` : undefined}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          readOnly={isReadOnly}
          disabled={isDisabled}
          className={isDisabled ? "disable-input" : ""}
          {...minMax}
          title={title}
        />
        <InputBorderSpan
          theme={ThemePalette}
          hasError={hasError}
          borderType={borderType}
        />
        <InputTitle isRequired={required} hasError={hasError}>
          {inputLabel}
        </InputTitle>
        {showLoadingState && (
          <Loader isCleared={isCleared}>
            <SpinLoader type={SpinLoaderTypes.small} />
          </Loader>
        )}
        {floatingComponent && isCleared && floatingComponentField()}
        {leadingComponent && leadingComponentField()}
        {errorMessage()}
        {requiredMessage()}
      </InputContainer>
    );
  };

  return inputComponent();
};
